import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { ReplaySubject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NdtHubBase } from '../base/ndt-hub.base';
import { NdtHubConnectionService } from '../ndt-hub-connection.service';
import {
   extensionCallQueueCallsChanged,
   extensionCallQueueCompleted,
   extensionCallQueueIsLoggedInChanged,
   extensionCallQueueStatusChanged,
} from './extension-call-queue-hub.actions';

@Injectable({ providedIn: 'root' })
export class ExtensionCallQueueHubService extends NdtHubBase {
   private registeredAccountId: number | null = null;

   private connectedRegistrationSub: Subscription | null = null;

   private connectedRegistrationSource: ReplaySubject<true> | null = null;

   constructor(public ndtHubService: NdtHubConnectionService, private store: Store, private ngZone: NgZone) {
      super(ndtHubService);

      this.ndtHubService.onBeforeConnected$.pipe(takeUntil(this.destroyed)).subscribe(() => {
         this.ensureConnectedRegistration();
      });
   }

   registerExtensionQueueIsLoggedInEvents(accountId: number | null): void {
      const nextAccountId = this.toNumber(accountId);
      const previousAccountId = this.registeredAccountId;

      this.registeredAccountId = nextAccountId;

      const reattachedToConnectedSubject = this.ensureConnectedRegistration();
      if (!reattachedToConnectedSubject && previousAccountId !== nextAccountId) {
         void this.switchRegisteredQueueGroup(previousAccountId, nextAccountId);
      }
   }

   unregisterExtensionQueueIsLoggedInEvents(accountId: number | null = this.registeredAccountId): void {
      const accountIdToLeave = this.toNumber(accountId);

      if (accountIdToLeave == null || this.registeredAccountId !== accountIdToLeave) {
         return;
      }

      this.registeredAccountId = null;
      void this.leaveQueueGroup(accountIdToLeave);
   }

   wireUpHubEvents(): void {
      this.ndtHubService.connection.on('LoggedInAgentsChanged', (data: any) => {
         if (this.registeredAccountId == null) {
            return;
         }

         const queueMemberId = this.toNumber(data?.queueMemberId);
         const queueExtensionId = this.toNumber(data?.extensionQueue?.extensionId);
         if (queueMemberId == null || queueExtensionId == null) {
            return;
         }

         this.ngZone.run(() => {
            this.store.dispatch(
               extensionCallQueueIsLoggedInChanged({
                  queueMemeberId: queueMemberId,
                  isLoggedIn: data.isLoggedIn,
                  queueExtensionId,
                  agentStatus: data?.queueExtensionStatus?.extensionStatus ?? null,
               })
            );
         });
      });
      this.ndtHubService.connection.on('LoggedInAgentStatusChanged', (data: any) => {
         if (this.registeredAccountId == null) {
            return;
         }

         const agentExtensionId = this.toNumber(data?.agentExtensionId);
         if (agentExtensionId == null) {
            return;
         }

         const extensionIds = (data?.extensionIds ?? [])
            .map((extensionId: any) => this.toNumber(extensionId))
            .filter((extensionId: number | null): extensionId is number => extensionId != null);

         this.ngZone.run(() => {
            this.store.dispatch(
               extensionCallQueueStatusChanged({
                  extensionIds,
                  agentExtensionId,
                  agentStatus: data.extensionStatus,
                  endWrapUpTime: data.endWrapUpTime,
               })
            );
         });
      });
      this.ndtHubService.connection.on('QueueCallsChanged', (data: any) => {
         if (this.registeredAccountId == null) {
            return;
         }

         const queueHistoryModel = data?.queueHistoryModel;
         const queueHistoryId = this.toNumber(queueHistoryModel?.queueHistoryId);
         const queueExtensionId = this.toNumber(queueHistoryModel?.queueExtensionId);
         if (queueHistoryId == null || queueExtensionId == null) {
            return;
         }

         this.ngZone.run(() => {
            this.store.dispatch(
               extensionCallQueueCallsChanged({
                  agentExtension: this.toExtensionReference(queueHistoryModel?.agentExtension),
                  queueExtension: this.toExtensionReference(queueHistoryModel?.queueExtension),
                  callDetailId: this.toNumber(queueHistoryModel?.callDetailId) ?? 0,
                  callerIdName: queueHistoryModel?.callerIdName,
                  callerIdNumber: queueHistoryModel?.callerIdNumber,
                  callerStatus: queueHistoryModel?.callerStatus,
                  queueExtensionId,
                  queueHistoryId,
                  startPosition: this.toNumber(queueHistoryModel?.startPosition) ?? 0,
                  callCompleteDateTime: queueHistoryModel?.callCompleteDateTime,
                  isCallBackRequested: queueHistoryModel?.isCallbackRequested,
                  callbackAttempts: this.toNumber(queueHistoryModel?.callbackAttempts) ?? 0,
                  enqueuedDateTime: queueHistoryModel?.enqueuedDateTime,
                  callDispositionId:
                     queueHistoryModel?.callDispositionId == null
                        ? null
                        : this.toNumber(queueHistoryModel?.callDispositionId),
                  answeredDateTime: queueHistoryModel?.answeredDateTime,
                  serverName: data?.serverName,
                  callerChannelId: data?.callerChannelId,
                  sipCallId: data?.sipCallId,
                  hasPreventEmptyQueue: data?.hasPreventEmptyQueue,
                  waitTime: data?.waitTime,
               })
            );
         });
      });
      this.ndtHubService.connection.on('QueueCallsCompleted', (data: any) => {
         if (this.registeredAccountId == null) {
            return;
         }

         const queueHistoryId = this.toNumber(data?.queueHistoryId);
         if (queueHistoryId == null) {
            return;
         }

         this.ngZone.run(() => {
            this.store.dispatch(
               extensionCallQueueCompleted({
                  queueHistoryId,
                  callCompleteDateTime: data.callCompleteDateTime,
               })
            );
         });
      });
   }

   private ensureConnectedRegistration(): boolean {
      if (this.connectedRegistrationSource === this.ndtHubService.connected) {
         return false;
      }

      this.connectedRegistrationSub?.unsubscribe();
      this.connectedRegistrationSource = this.ndtHubService.connected;
      this.connectedRegistrationSub = this.ndtHubService.connected.subscribe(() => {
         void this.joinRegisteredQueueGroup();
      });
      return true;
   }

   private async switchRegisteredQueueGroup(
      previousAccountId: number | null,
      nextAccountId: number | null
   ): Promise<void> {
      if (!this.isConnectionConnected()) {
         return;
      }

      if (previousAccountId != null && previousAccountId !== nextAccountId) {
         await this.leaveQueueGroup(previousAccountId);
      }

      if (nextAccountId != null) {
         await this.joinQueueGroup(nextAccountId);
      }
   }

   private async joinRegisteredQueueGroup(): Promise<void> {
      if (this.registeredAccountId == null) {
         return;
      }

      await this.joinQueueGroup(this.registeredAccountId);
   }

   private async joinQueueGroup(accountId: number): Promise<void> {
      if (!this.isConnectionConnected()) {
         return;
      }

      try {
         await this.ndtHubService.connection.invoke('JoinExtensionCallQueueMembersIsLoggedIn', accountId);
      } catch {
         // Ignore join failures here; the queue history component performs an API resync on reconnect.
      }
   }

   private async leaveQueueGroup(accountId: number): Promise<void> {
      if (!this.isConnectionConnected()) {
         return;
      }

      try {
         await this.ndtHubService.connection.invoke('LeaveExtensionCallQueueMembersIsLoggedIn', accountId);
      } catch {
         // Ignore leave failures because older hubs may not expose the leave method.
      }
   }

   private isConnectionConnected(): boolean {
      return this.ndtHubService.connection?.state === signalR.HubConnectionState.Connected;
   }

   private toExtensionReference(
      extension: any
   ): { extensionId: number; name: string | null; number: string | null } | null {
      const extensionId = this.toNumber(extension?.extensionId);
      if (extensionId == null) {
         return null;
      }

      return {
         extensionId,
         name: extension?.name ?? null,
         number: extension?.number ?? null,
      };
   }

   private toNumber(value: any): number | null {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
   }
}
